import { useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { faPlain, toman } from 'utilities/helper/format'
import { PulseCard, SectionError } from 'components/states'
import usePayables from 'modules/easyPay/hooks/usePayables'

/**
 * «پرداختنی‌های شما» — the reason the home page exists.
 *
 * Two rows, each with its own پرداخت button, and one «پرداخت همه» underneath.
 * The rows are deliberately NOT a taxonomy: row one is what is actually late,
 * row two is everything else. A member does not need the difference between a
 * cashbox share and an upcoming installment on the screen whose only question
 * is "is anything on fire".
 *
 * There is no separate urgency banner above this card. An earlier draft had
 * one, and it restated the overdue figure that is already the first row — on a
 * phone both were visible at once, so it read as two different debts.
 */
export default function PayablesCard() {
    const navigate = useNavigate()
    const { summary, isLoading, isError, refetch } = usePayables()

    if (isLoading) return <PulseCard />
    if (isError) return <SectionError onRetry={() => refetch()} />

    const go = (to: string, source: string) => {
        posthog.capture('payables_opened', { source })
        navigate(to)
    }

    if (!summary.hasAnything) {
        return (
            <div className="bg-white border border-divider rounded-card p-4 text-center">
                <p className="text-base font-bold text-heading">
                    پرداختنی‌های شما
                </p>
                <p className="mt-1 text-sm text-text-secondary">
                    فعلاً پرداختنی ندارید
                </p>
                {summary.recordCount > 0 && (
                    <p className="mt-2 text-xs text-text-muted">
                        {faPlain.format(summary.recordCount)} مورد را باید نزد
                        مدیر صندوق پرداخت کنید
                    </p>
                )}
            </div>
        )
    }

    return (
        <div className="bg-white border border-divider rounded-card p-4">
            {/* The title is a label; the number is the answer. Sized so the
                amount is what you see first. */}
            <p className="text-xs text-text-secondary text-center">
                پرداختنی‌های شما
            </p>
            <p className="mt-[2px] text-center text-[28px] font-bold text-heading leading-tight">
                {toman(summary.grandTotal)}
            </p>

            {summary.overdueCount > 0 && (
                <div className="mt-4 flex items-center gap-2 border-t border-divider pt-3">
                    <div className="flex-1 min-w-0 text-right">
                        {/* No flex-row-reverse: the page is already RTL, so
                            reversing would push the label to the left. */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-danger-on">
                                اقساط سررسید شده
                            </span>
                            <span className="text-[11px] font-bold bg-danger-soft text-danger-on rounded-full px-2 py-[1px]">
                                {faPlain.format(summary.overdueCount)} قسط
                            </span>
                        </div>
                        <p className="text-sm text-text-body mt-[2px]">
                            {toman(summary.overdueTotal)}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() =>
                            go('/easy-pay?filter=overdue', 'home_overdue')
                        }
                        className="shrink-0 bg-danger text-white rounded-full px-5 py-1 text-sm font-bold cursor-pointer"
                    >
                        پرداخت
                    </button>
                </div>
            )}

            {summary.soonCount > 0 && (
                <div className="mt-3 flex items-center gap-2 border-t border-divider pt-3">
                    <div className="flex-1 min-w-0 text-right">
                        <p className="text-sm font-bold text-heading">
                            {summary.soonLabel}
                        </p>
                        <p className="text-sm text-text-body mt-[2px]">
                            {faPlain.format(summary.soonCount)} مورد ·{' '}
                            {toman(summary.soonTotal)}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => go('/easy-pay?filter=dues', 'home_dues')}
                        className="shrink-0 bg-white border border-green-1 text-green-1 rounded-full px-5 py-1 text-sm font-bold cursor-pointer"
                    >
                        پرداخت
                    </button>
                </div>
            )}

            {summary.overdueCount > 0 && summary.soonCount > 0 && (
                <button
                    type="button"
                    onClick={() => go('/easy-pay?select=all', 'home_all')}
                    className="mt-4 w-full bg-green-1 text-white rounded-full py-2 text-sm font-bold cursor-pointer"
                >
                    پرداخت همه — {toman(summary.grandTotal)}
                </button>
            )}

            {summary.recordCount > 0 && (
                <p className="mt-3 text-center text-xs text-text-muted">
                    {faPlain.format(summary.recordCount)} مورد دیگر را باید نزد
                    مدیر صندوق پرداخت کنید
                </p>
            )}
        </div>
    )
}
